// texToImage — Convierte LaTeX en una imagen PNG nítida usando MathJax (SVG con
// trazos vectoriales, sin depender de fuentes web). Se usa para insertar
// fórmulas como OBJETO en la pizarra (Excalidraw).
import { mathjax } from "mathjax-full/js/mathjax.js";
import { TeX } from "mathjax-full/js/input/tex.js";
import { SVG } from "mathjax-full/js/output/svg.js";
import { liteAdaptor } from "mathjax-full/js/adaptors/liteAdaptor.js";
import { RegisterHTMLHandler } from "mathjax-full/js/handlers/html.js";
import { AllPackages } from "mathjax-full/js/input/tex/AllPackages.js";

let _mjDoc = null;
let _adaptor = null;

function getDoc() {
  if (_mjDoc) return { doc: _mjDoc, adaptor: _adaptor };
  _adaptor = liteAdaptor();
  RegisterHTMLHandler(_adaptor);
  const tex = new TeX({ packages: AllPackages });
  const svg = new SVG({ fontCache: "local" });
  _mjDoc = mathjax.document("", { InputJax: tex, OutputJax: svg });
  return { doc: _mjDoc, adaptor: _adaptor };
}

const EX_PX = 11; // pixeles por unidad "ex" (tamaño base de la fórmula)

// Devuelve { dataURL, width, height } con la fórmula renderizada como PNG.
export async function texToPng(latex, { color = "#1F2547", scale = 3 } = {}) {
  const { doc, adaptor } = getDoc();
  const node = doc.convert(latex, { display: true, em: 16, ex: 8, containerWidth: 1600 });
  let svgString = adaptor.innerHTML(node);

  // Dimensiones a partir de width/height en "ex".
  const wM = svgString.match(/width="([\d.]+)ex"/);
  const hM = svgString.match(/height="([\d.]+)ex"/);
  let w = wM ? parseFloat(wM[1]) * EX_PX : 200;
  let h = hM ? parseFloat(hM[1]) * EX_PX : 60;
  w = Math.max(24, Math.ceil(w));
  h = Math.max(18, Math.ceil(h));

  // Fijamos ancho/alto explícitos en px y el color (MathJax usa currentColor).
  // OJO: el <svg> ya trae un style="vertical-align:...", así que inyectamos el
  // color DENTRO de ese style (dos atributos style romperían el XML → img error).
  svgString = svgString
    .replace(/width="[\d.]+ex"/, `width="${w}"`)
    .replace(/height="[\d.]+ex"/, `height="${h}"`);
  if (/<svg[^>]*\sstyle="/.test(svgString)) {
    svgString = svgString.replace(/(<svg[^>]*\sstyle=")/, `$1color:${color};`);
  } else {
    svgString = svgString.replace("<svg ", `<svg style="color:${color}" `);
  }

  const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgString)));
  const img = new Image();
  img.width = w;
  img.height = h;
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
    img.src = svg64;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { dataURL: canvas.toDataURL("image/png"), width: w, height: h };
}
